import { Box, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import React from "react";
import theme from "Theme/AppTheme";
import { EmissionsConfigurationType } from "./EmissionsChips";

type Props = {
    isOpen: boolean;
    action: "disable" | "enable";
    type: EmissionsConfigurationType;
    name: string;
    affectedBodyNames?: string[];
    isLoading: boolean;
    onCancel: () => void;
    onConfirm: () => void;
};

const disableSummary = (type: EmissionsConfigurationType) =>
    type === "endpoint"
        ? "Nothing will be sent to this endpoint. In-flight retries finish."
        : "This emission body will stop sending.";

const enableSummary = (type: EmissionsConfigurationType) =>
    type === "endpoint"
        ? "Emission bodies that use this endpoint can send again as soon as this is on."
        : "This emission body starts sending as soon as this is on.";

const ConfigurationStatusDialog: React.FC<Props> = ({
    isOpen,
    action,
    type,
    name,
    affectedBodyNames = [],
    isLoading,
    onCancel,
    onConfirm,
}) => {
    const isDisable = action === "disable";
    const label = type === "endpoint" ? "endpoint" : "emission body";

    return (
        <AcxDialog
            isOpen={isOpen}
            onClose={onCancel}
            title={`${isDisable ? "Disable" : "Enable"} “${name}”?`}
            maxWidth="xs"
            fullWidth
            buttonsDisabled={isLoading}
            dialogContentChildren={
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "14px",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "13px",
                            lineHeight: 1.6,
                            color: theme.palette.gray.main,
                        }}
                    >
                        {isDisable ? (
                            <>
                                {disableSummary(type)} Receipts, failures and
                                rule filter evaluations stay available for
                                audit.
                            </>
                        ) : (
                            enableSummary(type)
                        )}
                    </Typography>
                    {isDisable &&
                        type === "endpoint" &&
                        affectedBodyNames.length > 0 && (
                            <Box
                                sx={{
                                    backgroundColor: theme.palette.error[50],
                                    border: `1px solid ${theme.palette.error[200]}`,
                                    borderRadius: "8px",
                                    padding: "12px 14px",
                                    fontSize: "12px",
                                    color: theme.palette.error[700],
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "6px",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "inherit",
                                        fontWeight: 700,
                                    }}
                                >
                                    {affectedBodyNames.length}{" "}
                                    {affectedBodyNames.length === 1
                                        ? "emission body"
                                        : "emission bodies"}{" "}
                                    will stop sending
                                </Typography>
                                {affectedBodyNames.map((bodyName, index) => (
                                    <Typography
                                        key={index}
                                        sx={{ fontSize: "inherit" }}
                                    >
                                        {bodyName}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    {isDisable &&
                        type === "endpoint" &&
                        affectedBodyNames.length === 0 && (
                            <Box
                                sx={{
                                    backgroundColor: theme.palette.gray[50],
                                    border: `1px solid ${theme.palette.gray[200]}`,
                                    borderRadius: "8px",
                                    padding: "12px 14px",
                                    fontSize: "12px",
                                    color: theme.palette.gray[600],
                                }}
                            >
                                No active emission bodies use this endpoint.
                            </Box>
                        )}
                    {!isDisable && type === "endpoint" && (
                        <Box
                            sx={{
                                backgroundColor: theme.palette.gray[50],
                                border: `1px solid ${theme.palette.gray[200]}`,
                                borderRadius: "8px",
                                padding: "12px 14px",
                                fontSize: "12px",
                                color: theme.palette.gray[600],
                            }}
                        >
                            Emission bodies disabled alongside this endpoint
                            stay disabled and have to be enabled individually.
                        </Box>
                    )}
                </Box>
            }
        >
            <AcxButton
                variant="contained"
                color={isDisable ? "error" : "primary"}
                loading={isLoading}
                onClick={onConfirm}
            >
                {isDisable ? "Disable" : "Enable"} {label}
            </AcxButton>
        </AcxDialog>
    );
};

export default ConfigurationStatusDialog;
